import { useCheckoutContext } from '../context/checkout.context';
import { useSettingsHook } from './settingsStorageHook';
import { Cornerstone } from '@rpi/api';
import dropin, {
  ActiveView,
  ChangeActiveViewPayload,
  Dropin,
} from 'braintree-web-drop-in';
import type {
  ButtonColorOption,
  ButtonShapeOption,
  ButtonSizeOption,
  ButtonStyle,
} from 'paypal-checkout-components';
import React, { useEffect } from 'react';

export function useBrainTreeHook(
  checkoutTotal: number,
  checkoutCurrency: Cornerstone.SupportedCurrency,
) {
  const [braintreeError, setBraintreeError] = React.useState<Error>();
  const [isPaymentRequestable, setIsPaymentRequestable] =
    React.useState<boolean>(false);
  const [selectedPaymentOption, setSelectedPaymentOption] =
    React.useState<ActiveView>('options');
  // The hook keeps its own reference to the braintree instance so it can run effects correctly
  // and return the onApplyPayment callback
  const [braintreeInstance, setBraintreeInstance] = React.useState<Dropin>();
  const onInstanceChanged = React.useCallback(
    (instance: Dropin | undefined) => {
      setBraintreeInstance(instance);
      if (instance) {
        instance.on('paymentMethodRequestable', () =>
          setIsPaymentRequestable(true),
        );
        instance.on('noPaymentMethodRequestable', () =>
          setIsPaymentRequestable(false),
        );
        instance.on('changeActiveView', (payload: ChangeActiveViewPayload) =>
          setSelectedPaymentOption(payload.newViewId),
        );
      } else {
        // this should be false when the instance is changed to undefined
        setIsPaymentRequestable(false);
      }
    },
    [],
  );

  const canUpdatePaypalConfig =
    selectedPaymentOption === 'options' ||
    selectedPaymentOption === 'paypal' ||
    selectedPaymentOption === 'paypalCredit';
  useEffect(() => {
    if (canUpdatePaypalConfig && braintreeInstance) {
      braintreeInstance.updateConfiguration('paypal', 'amount', checkoutTotal);
    }
  }, [braintreeInstance, canUpdatePaypalConfig, checkoutTotal]);

  useEffect(() => {
    if (canUpdatePaypalConfig && braintreeInstance) {
      braintreeInstance.updateConfiguration(
        'paypal',
        'currency',
        checkoutCurrency,
      );
    }
  }, [braintreeInstance, canUpdatePaypalConfig, checkoutCurrency]);

  const onApplyPayment = React.useCallback(async () => {
    if (braintreeInstance) {
      try {
        return await braintreeInstance.requestPaymentMethod();
      } catch (error) {
        setBraintreeError(error as Error);
      }
    }
  }, [braintreeInstance]);

  return {
    braintreeError,
    isPaymentRequestable,
    billingAddressRequired: selectedPaymentOption === 'card',
    onApplyPayment,
    onInstanceChanged,
    setBraintreeError,
  };
}

const brainTreeButtonStyle = {
  color: 'blue' as ButtonColorOption.Blue,
  shape: 'rect' as ButtonShapeOption.Rect,
  size: 'medium' as ButtonSizeOption.Medium,
  tagline: false,
} satisfies Partial<ButtonStyle>;

export function BrainTreeComponent() {
  const { onInstanceChanged, checkoutData, setBraintreeError } =
    useCheckoutContext();
  const [initialTotalState] = React.useState<number>(checkoutData.total.amount);
  const [initialCurrencyState] = React.useState<Cornerstone.SupportedCurrency>(
    checkoutData.currency,
  );
  // The braintree component needs a ref to an empty div
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Used to prevent initializing while it's still initializing
  const isCreating = React.useRef<boolean>(false);
  // Prevent from creating while tearing down
  const teardownPromise = React.useRef<Promise<void> | undefined>(undefined);

  const { settings } = useSettingsHook();
  const userLocale = settings?.locale ?? 'en_US';

  // Creating as a ref since it's an external (non-reactive) resource that needs to be cleaned up
  // in the Effect that creates it
  const braintreeInstanceRef = React.useRef<Dropin>();
  // This effect creates the braintree dropin
  // It's done on this React component so the lifecycle of the dropin is tied to it
  React.useEffect(() => {
    async function createBraintreeInstance(container: HTMLDivElement) {
      try {
        isCreating.current = true;
        if (teardownPromise.current) {
          // If the previous one is still tearing down, wait for that to finish
          await teardownPromise.current;
        }

        // Not using abort signal because dropin.create doesn't take one, and that's the important bit
        const { data } = await Cornerstone.createBraintreeClientToken();
        if (!data.token) {
          setBraintreeError(new Error('braintree token is undefined'));
          return;
        }

        const instance = await dropin.create({
          authorization: data.token,
          container,
          dataCollector: true,
          locale: userLocale,
          paypal: {
            flow: 'checkout',
            amount: initialTotalState,
            currency: initialCurrencyState,
            buttonStyle: brainTreeButtonStyle,
          },
        });
        braintreeInstanceRef.current = instance;
        onInstanceChanged(instance);
      } catch (error) {
        setBraintreeError(error as Error);
      } finally {
        isCreating.current = false;
      }
    }

    if (containerRef.current && !isCreating.current) {
      createBraintreeInstance(containerRef.current);
    }

    return function () {
      if (!teardownPromise.current) {
        setBraintreeError(undefined);
        // We want the current value of ref, since it's changed outside of rendering
        // Otherwise we'd get the value before it finished creating
        teardownPromise.current = braintreeInstanceRef.current
          ?.teardown()
          .then(() => onInstanceChanged(undefined)); // otherwise teardown can get called twice
      }
    };
  }, [
    userLocale,
    initialTotalState,
    initialCurrencyState,
    setBraintreeError,
    onInstanceChanged,
  ]);

  return <div ref={containerRef} />;
}
